import type { Component } from "svelte";

import Dashboard from "../pages/Dashboard.svelte";
import { argusAbsichten, argusRouten } from "../module/argus/routen";
import { medienAbsichten, medienRouten } from "../module/medien/routen";

// PageLoader: Lazy-Import einer Page. Seit dem Code-Splitting
// (2026-07) importiert routes.ts die Pages nicht mehr statisch --
// jede Page ist ein eigener Vite-Chunk, den erst die Navigation
// (bzw. der Idle-Prefetch in App.svelte) laedt. Nur Dashboard
// bleibt eager im Haupt-Chunk: es ist die Landing-Page, ein
// zweiter Roundtrip vor dem ersten Paint waere ein Rueckschritt.
export type PageLoader = () => Promise<{ default: Component }>;

export interface RouteItem {
  path: string;
  label: string;
  component: PageLoader;
  // hidden=true: the route resolves (so URLs work and findRoute
  // returns it), but the Sidebar skips it. Used for Level-3
  // ("Deep-Inside") pages of the Status-Card-Pattern that are
  // reachable from their parent overview, not from a top-level nav.
  hidden?: boolean;
  // inMenu=false: route resolves, aber wird in Navigations-Menues
  // (Rechtsklick, Cmd+K) nicht gelistet. Sinnvoll fuer Tab-Aliase
  // wie /settings/hosts -- gleiche Komponente, andere URL, ein
  // Eintrag im Menue reicht.
  inMenu?: boolean;
  // tabWurzel: diese Route ist ein REGISTER seiner Seite, keine eigene
  // Seite. Erkennbar daran, dass sie dieselbe Komponente laedt wie ihr
  // Elternpfad -- /infra/network/verlauf ist der Reiter "Verlauf" AUF
  // /infra/network.
  //
  // Warum das hier stehen muss: in der Shell ist jede Seite ein Tab.
  // Ohne diese Angabe bekaeme jeder Reiterwechsel einen EIGENEN Tab
  // ("Netzwerk" neben "Verlauf"), obwohl man dieselbe Seite
  // bedient (Tim 15.08.). Die Shell haelt Register deshalb im selben
  // Tab und tauscht nur den Pfad.
  //
  // Bewusst explizit statt hergeleitet: die Komponente laesst sich zur
  // Laufzeit nicht vergleichen (jede Route traegt ihre eigene
  // Lade-Funktion), und "Unterpfad = Register" waere falsch --
  // /zuhause/wetter/blitze ist eine eigene Seite.
  tabWurzel?: string;
  // aliases: zusaetzliche Such-Stichworte fuer die TopBar-Suche.
  // Erlaubt englische Begriffe (Login -> Anmeldung), Synonyme
  // (Hosts -> Cluster) und tippfreundliche Kuerzel. Nur fuer die
  // Suche relevant -- in keiner UI sichtbar.
  aliases?: string[];
  // eigenerPfad: die Seite trägt ihren Pfad selbst im Kopf (Claude-Chat:
  // „Claude ›" vor dem Titel) -- die Pfadzeile der Shell darüber entfällt
  // (Tim 19.10.2026: „die breite Infoleiste oben kompakter … nimmt zu viel
  // Höhe vom Chat weg").
  eigenerPfad?: boolean;
}

export interface RouteSection {
  title: string; // empty string = no header (e.g. Dashboard alone at top)
  items: RouteItem[];
}

// Information architecture for v2. The Python original was an unordered
// pile of features; this groups by mental model:
//   - Dashboard: at-a-glance, the everyday landing
//   - Infrastruktur: hardware-state + interventions
//   - Sicherheit: DNS-Filter, fail2ban, Auth-Inspector
//   - IoT: sensors + actuators + Wetter
//   - Aufgaben: cron + manual + history + Automatisierungen
//   - Tools: power-user instruments (deep-tools-look later)
//   - Einstellungen: configuration
//
// IA-Refactor 2026-05-05 (Navigation-Redesign Commit 1):
//   - AdGuard: /infra/adguard -> /sicherheit/dns (eigene Sektion)
//   - Lightning: /infra/lightning -> /zuhause/wetter (Wetter-Page in
//     Commit 2 baut Lightning als Card unten ein; bis dahin zeigt
//     /zuhause/wetter weiter die Lightning-Komponente, Label "Blitze")
//   - AC Infinity: /zuhause/acinfinity -> /zuhause/aktoren/acinfinity (Sub
//     der Aktoren-Sektion, hidden -- Einstieg ueber Aktoren-Hub)
//   - Automation: /zuhause/automation -> /system/automatiken
//   - Alte URLs werden ueber `redirects` abgefangen, damit Bookmarks
//     mind. ein Release lang weiterleben.
export const sections: RouteSection[] = [
  {
    // Dashboard ist via Homelab-Logo links in der Toolbar erreichbar
    // (Klick = Home). Sidebar-Eintrag damit redundant; hidden:true
    // sorgt dafuer, dass die Section verschwindet.
    title: "",
    items: [{ path: "/", label: "Dashboard", component: () => Promise.resolve({ default: Dashboard }), hidden: true }],
  },
  {
    title: "Infrastruktur",
    items: [
      // Section-Hub (Slice 1 IA-Refactor 2026-05-14). Listet alle
      // Sub-Pages als Karten -- gleichberechtigter Einstieg neben den
      // Dashboard-Status-Cards. Damit ist das alte Single-Path-of-
      // Access aufgegeben: Pages haben jetzt zwei legitime Eingaenge
      // (Dashboard-Status-Card und Section-Hub).
      {
        path: "/infra",
        label: "Infrastruktur",
        component: () => import("../pages/BereichsUebersicht.svelte"),
        aliases: ["Infrastructure", "Infra"],
      },
      // Cluster ist die Detail-Page der Hosts-Card auf der Übersicht.
      // Ueber den Hub jetzt direkt erreichbar; hidden bleibt true,
      // weil die Sidebar/Section-Liste die nicht doppelt zeigen soll
      // (Hub fasst sie zusammen).
      {
        path: "/infra/cluster",
        label: "Cluster",
        component: () => import("../pages/Cluster.svelte"),
        hidden: true,
        aliases: ["Hosts", "Server", "Maschinen", "Nodes"],
      },
      // Ein einzelner Rechner: seit 29.09.2026 keine eigene Seite mehr,
      // sondern die Cluster-Seite mit seiner Zeile offen (Detail inline,
      // docs/gestaltung.md Regel 4). Die Adresse bleibt, weil Explorer-Baum
      // und Meldungen ("Rechner offline") hierher verlinken.
      //
      // tabWurzel: darum auch kein eigener Tab. Ein Klick auf „Homeserver2"
      // im Baum öffnete bis 04.10.2026 einen Tab „homeserver2" NEBEN dem
      // Tab „Cluster" -- zweimal dieselbe Seite, einmal mit offener Zeile
      // (Tim: „Das macht keinen Sinn mehr … nur die Clusterseite öffnen
      // und denjenigen Node expandieren"). Wie ein Register bleibt der
      // Rechner jetzt im Tab seiner Seite; nur der Pfad wechselt, und die
      // Seite klappt die Zeile auf.
      {
        path: "/infra/cluster/:host_id",
        tabWurzel: "/infra/cluster",
        label: "Host",
        component: () => import("../pages/Cluster.svelte"),
        hidden: true,
      },
      { path: "/infra/network", label: "Netzwerk", component: () => import("../pages/Network.svelte"), hidden: true, aliases: ["Network", "WAN", "LAN", "OpenWrt"] },
      { path: "/infra/docker", label: "Docker", component: () => import("../pages/Docker.svelte"), hidden: true, aliases: ["Container", "Compose"] },
      { path: "/infra/storage", label: "Storage", component: () => import("../pages/Storage.svelte"), hidden: true, aliases: ["Speicher", "Disks", "Festplatten", "ZFS", "Pool", "SMART", "Snapshots"] },
      { path: "/infra/flash", label: "Flash-Werkbank", component: () => import("../pages/FlashBench.svelte"), hidden: true, aliases: ["Flash", "ESPHome", "Shelly", "FTDI", "Firmware", "Werkbank", "Geräte flashen"] },
      { path: "/infra/agents", label: "Agents", component: () => import("../pages/Agents.svelte"), hidden: true, aliases: ["Agenten", "MQTT-Agenten"] },
      // Link-Health ist ein Register der Netzwerk-Seite (2026-07-18);
      // die URL bleibt als Bookmark-Ziel und rendert Network.svelte,
      // das intern aufs Register schaltet (AdGuard-Muster).
      { path: "/infra/network/verlauf", tabWurzel: "/infra/network", label: "Verlauf", component: () => import("../pages/Network.svelte"), hidden: true, inMenu: false, aliases: ["Link-Health", "Link-Verlauf", "Verbindungsverlauf", "Link", "Carrier", "Ethernet", "Netzwerk-Diagnose"] },
      { path: "/infra/topologie", label: "Topologie", component: () => import("../pages/Topologie.svelte"), hidden: true, aliases: ["Topology", "Netzwerk-Graph", "Network Map", "Karte", "Graph", "Cluster-Karte"] },
      { path: "/infra/status", label: "Systemstatus", component: () => import("../pages/Status.svelte"), hidden: true, aliases: ["Status", "Systemstatus", "Verbindungen", "Link-Status", "Connectivity", "Erreichbarkeit", "Uptime", "URL-Monitor", "Reachability", "Health", "Zustand"] },
      { path: "/infra/netzfrequenz", label: "Netzfrequenz", component: () => import("../pages/Netzfrequenz.svelte"), hidden: true, aliases: ["Frequenz", "Stromnetz", "Hertz", "Hz", "50 Hz", "Verbundnetz", "Grid", "Mains Frequency", "Blackout"] },
      // Einstellungen-Dedupe 2026-07-18: die Infra-Settings sind Register
      // ihrer Domain-Seiten (AdGuard-Muster) -- der Settings-Hub ist weg,
      // die URLs rendern die jeweilige Wirts-Seite mit aktivem Register.
      // Register der Docker-Seite (02.10.2026: im Baum unter Docker).
      { path: "/infra/docker/stacks", tabWurzel: "/infra/docker", label: "Stacks", component: () => import("../pages/Docker.svelte"), hidden: true, inMenu: false, aliases: ["Compose-Stacks", "docker compose"] },
      { path: "/infra/docker/netzwerke", tabWurzel: "/infra/docker", label: "Netzwerke", component: () => import("../pages/Docker.svelte"), hidden: true, inMenu: false, aliases: ["Docker-Netzwerke", "Bridge"] },
      { path: "/infra/docker/aufraeumen", tabWurzel: "/infra/docker", label: "Aufräumen", component: () => import("../pages/Docker.svelte"), hidden: true, inMenu: false, aliases: ["Docker aufräumen", "Images", "Volumes", "Werkzeuge", "Behalten", "prune"] },
    ],
  },
  {
    // Sicherheit als eigene Top-Sektion: heute Status-Card +
    // Inspector + DNS-Filter. fail2ban-Detailpage und Auth-Page
    // werden hier andocken, ohne dass die IA nochmal umgebaut
    // werden muss.
    title: "Sicherheit",
    items: [
      // Section-Hub (Slice 1 IA-Refactor). /sicherheit ist die
      // gemeinsame Uebersicht; /security bleibt fuer die Status-
      // Card-Detail-Page (Live-Stats, fail2ban, Auth-Vesuche).
      {
        path: "/sicherheit",
        label: "Sicherheit",
        component: () => import("../pages/BereichsUebersicht.svelte"),
        aliases: ["Security", "Perimeter"],
      },
      // Status-Card-Detail (heute /security). Erreichbar ueber
      // Dashboard-Card + Hub.
      {
        path: "/security",
        label: "Übersicht",
        component: () => import("../pages/Security.svelte"),
        hidden: true,
        aliases: ["fail2ban", "Auth-Versuche", "Login-Versuche", "Login attempts", "Bans"],
      },
      // Forensik haengt direkt am Sicherheit-Hub (Karte) -- die URL
      // spiegelt das seit 2026-07-02, vorher /security/inspector
      // (Breadcrumb zeigte faelschlich "Uebersicht" dazwischen).
      {
        path: "/sicherheit/forensik",
        label: "Forensik",
        component: () => import("../pages/SecurityInspector.svelte"),
        hidden: true,
        aliases: ["Inspector", "Werkzeuge", "IP-Whitelist", "Tools"],
      },
      // Geheimnis-Speicher: Dienst-Zugangsdaten, verschluesselt.
      // Bewusst hier und NICHT unter /profil/zugang -- dort geht es um
      // DEINEN Zugang zum Dashboard (Passkeys, Sitzungen), hier um
      // Zugangsdaten fuer fremde Dienste. Zwei verschiedene Dinge im
      // selben Regal sucht man spaeter an der falschen Stelle.
      {
        path: "/sicherheit/geheimnisse",
        label: "Geheimnisse",
        component: () => import("../pages/SettingsSecrets.svelte"),
        aliases: ["Secrets", "Token", "API-Token", "Zugangsdaten", "Passwoerter", "Credentials", "Schluessel"],
      },
      // DNS-Filter (vorher /infra/adguard): AdGuard im Homelab-Design
      // (docs/dns.md) -- Lage, Query-Log, Filterlisten, lokale Namen,
      // Geräte; freigeben/sperren, Schutz pausieren, Listen aktualisieren.
      {
        path: "/sicherheit/dns",
        label: "DNS",
        component: () => import("../pages/AdGuard.svelte"),
        hidden: true,
        aliases: ["AdGuard", "DNS-Filter", "Querylog", "Block-Listen", "Blocklist", "Filterlisten", "Upstreams", "Werbeblocker", "Domain freigeben"],
      },
      // Anmeldung + Push sind 2026-05-14 nach /profil/* gewandert --
      // sind persoenliche Account-Sachen, keine Sicherheits-Perimeter-
      // Einstellungen. Redirects unten fangen alte Bookmarks.
      // AdGuard-Settings sind seit Slice 6 ein Tab AUF der DNS-Page,
      // nicht mehr eine eigene Page. Eigener Route-Eintrag damit der
      // Tab eine Bookmark-URL hat (AdGuard.svelte schaltet intern um).
      // Netzwerk-Erkennung: selbstlernender Geraete-Bestand + IP-Sperrliste.
      {
        path: "/sicherheit/netsec",
        label: "Netzwerk-Erkennung",
        component: () => import("../pages/Netsec.svelte"),
        hidden: true,
        aliases: ["ARP", "MAC", "Geräte", "Netsec", "CrowdSec", "Sperrliste", "new_device", "Anlernen", "Router zuhause"],
      },
    ],
  },
  {
    // Zuhause (Nav-Konzept 2026-07-18, vorher "IoT"): alles was im
    // Wohnraum lebt -- Geraete, Sensorik, Aktorik, Szenen, Wetter,
    // Energie. Sektion = Objektwelt, nicht Werkzeugtyp; Technologien
    // (Zigbee/Z-Wave/ESPHome) sind Filter auf der Geraete-Seite,
    // keine Menuepunkte.
    title: "Zuhause",
    items: [
      {
        path: "/zuhause",
        label: "Zuhause",
        component: () => import("../pages/BereichsUebersicht.svelte"),
        aliases: ["IoT", "Smart Home", "Hausautomatisierung", "Home"],
      },
      // /zuhause/sensoren ist Ebene 2 der Sensoren-Card auf dem
      // Dashboard. Selbst dashboard-artig: drei Sub-Cards (Klima/
      // CO₂/Lipoguard), jede klickbar zur Ebene-3-Sensor-Liste.
      // Single Path of Access — die Sub-Domain-Pages sind nur über
      // /zuhause/sensoren erreichbar, in der Sidebar versteckt.
      {
        path: "/zuhause/sensoren",
        label: "Sensoren",
        component: () => import("../pages/Sensoren.svelte"),
        hidden: true,
        aliases: ["Sensors", "Messwerte"],
      },
      {
        path: "/zuhause/aktoren",
        label: "Aktoren",
        component: () => import("../pages/Aktoren.svelte"),
        hidden: true,
        aliases: ["Actors", "Actuators", "Schalter", "Switches", "Steckdosen"],
      },
      {
        path: "/zuhause/aktoren/:name",
        label: "Aktor",
        component: () => import("../pages/Aktor.svelte"),
        hidden: true,
      },
      // AC Infinity ist Sub-Page der Aktoren-Sektion (vorher
      // sichtbarer Top-Eintrag /zuhause/acinfinity). Eigene Steuer-
      // Semantik bleibt, Einstieg jetzt ueber Aktoren-Hub.
      // Static-Match gewinnt vor /zuhause/aktoren/:name.
      {
        path: "/zuhause/aktoren/acinfinity",
        label: "AC Infinity",
        component: () => import("../pages/AcInfinity.svelte"),
        hidden: true,
        aliases: ["Lüfter", "Fan", "Grow", "Zelt"],
      },
      {
        path: "/zuhause/climate",
        label: "Klima · Räume",
        component: () => import("../pages/Climate.svelte"),
        hidden: true,
        // Nicht im Nav-Menue: die Klima-Sensoren stecken schon in der
        // Sensoren-Page (/zuhause/sensoren). Diese Page bleibt als Sensor-
        // Detail-Ziel (?sensor=) erreichbar, nur der redundante Menue-
        // Eintrag faellt weg.
        inMenu: false,
        aliases: ["Climate", "Temperatur", "Temperature", "Luftfeuchtigkeit", "Humidity", "Räume", "Rooms"],
      },
      {
        path: "/zuhause/co2monitor",
        inMenu: false,
        label: "CO₂",
        component: () => import("../pages/Co2Monitor.svelte"),
        hidden: true,
        aliases: ["CO2", "Luftqualität", "Air Quality"],
      },
      // Funkmelder: Bewegung aus dem WLAN-Kanal (ESP32 + ESPectre). Eigene
      // Seite statt Zeile bei den Sensoren, weil man hier die Kurve gegen
      // die Schwelle braucht -- live, zum Einstellen.
      {
        path: "/zuhause/funkmelder",
        label: "Funkmelder",
        component: () => import("../pages/Funkmelder.svelte"),
        hidden: true,
        aliases: ["Bewegung", "Bewegungsmelder", "Motion", "WLAN-Sensing", "CSI", "ESPectre", "Präsenz", "Anwesenheit"],
      },
      // Grundriss: die Wohnung als Plan und als kleine räumliche Ansicht
      // (lib/grundriss). Der Ort, an dem Geräte ihren Platz bekommen.
      {
        path: "/zuhause/grundriss",
        label: "Grundriss",
        component: () => import("../pages/Grundriss.svelte"),
        hidden: true,
        aliases: ["Wohnung", "Plan", "Räume", "Zimmer", "Floorplan", "3D", "Lageplan"],
      },
      // Der 3D-Drucker steht seit 18.10.2026 in der Werkstatt (unten).
      // Kalender (22.10.2026, Tim: „einen Kalender bauen, in dem wir sowas
      // vormerken können"): Termine von Tim und Claude, erinnert per Push
      // und Hinweis unter den Meldungen (docs/kalender.md).
      {
        path: "/zuhause/kalender",
        label: "Kalender",
        component: () => import("../pages/Kalender.svelte"),
        hidden: true,
        aliases: ["Termine", "Termin", "Erinnerung", "Erinnerungen", "Vormerken", "Datum", "Planer", "Agenda", "Calendar", "Reminder"],
      },
      // Medien (01.11.2026, docs/medien.md): Filme und Serien statt Jellyfin
      // -- die Seiten stehen in module/medien/routen.ts.
      ...medienRouten,
      {
        path: "/zuhause/lipoguard",
        inMenu: false,
        label: "Lipoguard",
        component: () => import("../pages/Lipoguard.svelte"),
        hidden: true,
        aliases: ["LiPo", "Akku", "Battery", "Spannung"],
      },
      {
        path: "/zuhause/waterleak",
        inMenu: false,
        label: "Wasserlecks",
        component: () => import("../pages/Waterleak.svelte"),
        hidden: true,
        aliases: ["Water Leak", "Leckage", "Wassermelder"],
      },
      // Wetter-Sektion (vorher /infra/lightning): aktueller Stand,
      // 24-h-Strip, 7-Tage-Bars und Live-Blitz-Card unten -- ein
      // einziger /v2-api/weather-Call liefert beides (OpenMeteo +
      // Lightning gemerged). Sichtbar in der Sidebar.
      {
        path: "/zuhause/wetter",
        label: "Wetter",
        component: () => import("../pages/Wetter.svelte"),
        aliases: ["Weather", "Forecast", "Vorhersage"],
      },
      // Blitz-Detail-Page mit voller Strike-Tabelle und Track-/Alert-
      // Radius-Editor. Deep-Link aus der Wetter-Page (Lightning-Card
      // -> "Konfiguration"); kein eigener Sidebar-Eintrag.
      {
        path: "/zuhause/wetter/blitze",
        label: "Blitze",
        component: () => import("../pages/Lightning.svelte"),
        hidden: true,
        aliases: ["Lightning", "Gewitter", "Thunder"],
      },
      // Vollbild-Wetterkarte (windy.com Embed). Deep-Link aus der
      // Wetter-Page; kein eigener Sidebar-Eintrag, weil die Page schon
      // unter Wetter gruppiert ist.
      {
        path: "/zuhause/wetter/karte",
        label: "Karte",
        component: () => import("../pages/WetterKarte.svelte"),
        hidden: true,
        aliases: ["Map", "Radar", "Windy", "Niederschlag", "Regenradar"],
      },
      // Energie/Zigbee/Z-Wave haben eigene Cards auf dem Dashboard
      // (heute provisorisch als Ebene 2 selbst). Eigene Ebene-2-
      // Bündel-Pages folgen wenn ihre Sub-Domains wachsen.
      { path: "/zuhause/energy", label: "Energie", component: () => import("../pages/Energy.svelte"), hidden: true, aliases: ["Energy", "Power", "Strom", "Verbrauch", "PV", "Solar"] },
      {
        path: "/zuhause/ambient",
        label: "Ambient & Szenen",
        component: () => import("../pages/Ambient.svelte"),
        hidden: true,
        aliases: ["Scenes", "Szenen", "Lichter", "Lights", "Hue", "Lighting"],
      },
      // Geraete-Seite: EINE vereinheitlichte Sicht ueber alle
      // Integrations-Quellen (Sektionen = Zigbee/Z-Wave/ESPHome),
      // Zeilen-Klick oeffnet den Verlauf. Ersetzt die Integrationen-
      // Karten-Ebene (HA-Erbe, 2026-07-17); /zuhause/integrationen
      // redirected hierauf.
      {
        path: "/zuhause/geraete",
        label: "Geräte",
        component: () => import("../pages/Geraete.svelte"),
        hidden: true,
        aliases: ["Devices", "Integrationen", "Integrations", "Bridges", "Sensoren", "Verlauf"],
      },
      // Einstellungen-Dedupe 2026-07-18: HA-Anbindung = Register der
      // Geraete-Seite, AC-Infinity-Cloud = Register der ACI-Seite.
    ],
  },
  {
    // Werkstatt (18.10.2026, Tim): was gebaut wird. Konstruktionen (CAD aus
    // dem Repo tim/konstruktionen, docs/konstruktionen.md) und der
    // 3D-Drucker, der dafür aus Zuhause hierher zog; später der Slicer
    // (Bambu Studio). Die Übersicht ist wie bei den anderen Bereichen
    // BereichsUebersicht -- am Schreibtisch zeigt der Pfad den Baum.
    title: "Werkstatt",
    items: [
      {
        path: "/werkstatt",
        label: "Werkstatt",
        component: () => import("../pages/BereichsUebersicht.svelte"),
        aliases: ["Workshop", "Bauen", "Basteln", "Maker", "Fertigung"],
      },
      // Konstruktionen: Übersicht aller Modelle; eine Konstruktion ist eine
      // eigene Seite (Kategorie + Name aus dem Repo).
      {
        path: "/werkstatt/konstruktionen",
        label: "Konstruktionen",
        component: () => import("../pages/Konstruktionen.svelte"),
        hidden: true,
        aliases: ["Konstruktion", "CAD", "SolidCraft", "3D-Modell", "Modell", "Viewer", "STEP", "Druckteile", "Fusion"],
      },
      {
        path: "/werkstatt/konstruktionen/:kategorie/:name",
        label: "Konstruktion",
        component: () => import("../pages/Konstruktion.svelte"),
        hidden: true,
        inMenu: false,
        // „Konstruktionen ›" steht schwebend über dem Modell (Tim
        // 20.10.2026: „Kann nur hier die Pfadangabe ganz oben weg").
        eigenerPfad: true,
      },
      // Kaufteile (19.10.2026, Tim): was an Schrauben, Muttern, Einsätzen
      // da ist -- seit 20.10.2026 in der DB und hier bearbeitbar, Claude
      // plant nur damit (docs/konstruktionen.md, „Kaufteile").
      {
        path: "/werkstatt/kaufteile",
        label: "Kaufteile",
        component: () => import("../pages/Kaufteile.svelte"),
        hidden: true,
        aliases: ["Schrauben", "Muttern", "Gewindeeinsätze", "ruthex", "Einschmelzmuttern", "Sortiment", "Teileliste", "Bestand", "Lager", "Normteile", "Scheiben", "Inventar"],
      },
      // 3D-Drucker (Bambu Lab P1S, internal/bambu, docs/drucker.md): was er
      // gerade druckt, AMS, Kamera auf Anforderung, Verlauf der Aufträge.
      // Bis 18.10.2026 /zuhause/drucker (Weiterleitung unten).
      {
        path: "/werkstatt/drucker",
        label: "3D-Drucker",
        component: () => import("../pages/Drucker.svelte"),
        hidden: true,
        aliases: ["Drucker", "Bambu", "Bambu Lab", "P1S", "3D-Druck", "Printer", "AMS", "Filament", "Druckauftrag"],
      },
    ],
  },
  // Argus (28.10.2026, docs/argus-modul.md): der Modellbau für Tims
  // Kamerasystem als Modul -- die Seiten stehen in module/argus/routen.ts.
  argusRouten,
  {
    // System (Nav-Konzept 2026-07-18): "laeuft der Laden?" -- fasst
    // die frueheren Sektionen Aufgaben (Tasks/Runs/Backups/
    // Automatiken) und Diagnose (Logs/Ereignisse/DB/Terminal/
    // Werkzeuge) zusammen. Das Cmd-K-Menue gruppiert die Spalte in
    // "Aufgaben" und "Diagnose" (NavSearchPanel.buildSystemGroups).
    title: "System",
    items: [
      {
        path: "/system",
        label: "System",
        component: () => import("../pages/BereichsUebersicht.svelte"),
        aliases: ["Aufgaben", "Tasks", "Jobs", "Diagnose", "Diagnostics", "Tools", "Betrieb"],
      },
      { path: "/system/tasks", label: "Tasks", component: () => import("../pages/Tasks.svelte"), hidden: true, aliases: ["Aufgaben", "Cron", "Jobs"] },
      { path: "/system/runs", label: "Läufe", component: () => import("../pages/Runs.svelte"), hidden: true, aliases: ["Runs", "History", "Verlauf", "Lauf-Historie"] },
      { path: "/system/backups", label: "Backups", component: () => import("../pages/BackupPage.svelte"), hidden: true, aliases: ["Backup", "Sicherung", "Datensicherung", "Datenfluss", "ZFS-Backup", "Offsite", "Restore", "Snapshots"] },
      // Wiederherstellungsprobe (17.10.2026, docs/restore-probe.md): jede
      // Kette einmal pro Woche wirklich zurückgespielt und verglichen. Eine
      // eigene Seite, im Baum unter Backups (Pfad-Schachtelung).
      {
        path: "/system/backups/proben",
        label: "Proben",
        component: () => import("../pages/BackupProben.svelte"),
        hidden: true,
        aliases: ["Restore-Test", "Wiederherstellungsprobe", "Wiederherstellung testen", "Backup-Test", "Backup testen", "Ernstfall", "Gegenprobe", "Prüfsummen", "Fingerabdruck"],
      },
      // Abdeckung (28.10.2026, docs/abdeckung.md): je Rechner, was Daten
      // trägt, und welche Kette es sichert -- die Lücken zuerst.
      {
        path: "/system/backups/abdeckung",
        label: "Abdeckung",
        component: () => import("../pages/BackupAbdeckung.svelte"),
        hidden: true,
        aliases: ["Was ist gesichert", "Ungesichert", "Ohne Backup", "Ohne Sicherung", "Lücken", "Backup-Abdeckung", "Was liegt wo"],
      },
      // network wiederaufbauen (18.10.2026, docs/network-wiederaufbau.md):
      // derselbe geführte Durchlauf wie im Recovery-Tool, für network selbst.
      {
        path: "/system/backups/network",
        label: "network wiederaufbauen",
        component: () => import("../pages/NetworkWiederaufbau.svelte"),
        hidden: true,
        aliases: ["Recovery-Tool wiederherstellen", "network neu aufsetzen", "SD-Karte network", "Raspberry Pi wiederherstellen", "network kaputt", "Wiederaufbau network"],
      },
      // Archiv (23.10.2026, docs/archiv.md): archivierte Dienste unter
      // pool/archiv und die Reste aufgelöster Backup-Ketten. Neben Backups.
      {
        path: "/system/archiv",
        label: "Archiv",
        component: () => import("../pages/Archiv.svelte"),
        hidden: true,
        aliases: ["Archive", "archiviert", "Home Assistant Archiv", "Node-RED Archiv", "Backup-Reste", "verwaiste Backups", "Kette aufgelöst", "Zurückholen", "abgeschaltete Dienste"],
      },
      // Automatiken: regelbasierte Aufgaben (wenn X, dann Y). Vorher
      // /aufgaben/automatisierungen; Label folgt dem UI-Wording
      // "Automatiken" (2026-07).
      {
        path: "/system/automatiken",
        label: "Automatiken",
        component: () => import("../pages/Automation.svelte"),
        hidden: true,
        aliases: ["Automations", "Automatisierungen", "Rules", "Regeln", "Trigger"],
      },
      // Server (15.10.2026, docs/server.md): der Rechner, auf dem das
      // Dashboard laeuft, und der Dienst selbst -- Last, Speicher,
      // Datenbank, Container, Journal, Neustart des Dienstes.
      {
        path: "/system/server",
        label: "Server",
        component: () => import("../pages/Server.svelte"),
        hidden: true,
        aliases: ["Server-Info", "Dienst", "forge", "Last", "Auslastung", "Arbeitsspeicher", "RAM", "CPU", "Temperatur", "Schreibrate", "Datenbankgröße", "WAL", "Freiliste", "Neustart", "Dienst neu starten", "homelab neu starten", "Version"],
      },
      { path: "/system/logs", label: "Logs", component: () => import("../pages/Logs.svelte"), hidden: true, aliases: ["Protokolle", "Journal"] },
      { path: "/system/meldungen", label: "Meldungen", component: () => import("../pages/Meldungen.svelte"), hidden: true, aliases: ["Warnungen", "Alerts", "Benachrichtigungen", "Hinweise", "Alarme"] },
      { path: "/system/events", label: "Ereignisse", component: () => import("../pages/Events.svelte"), hidden: true, aliases: ["Events", "Audit", "Audit Log"] },
      // DB-Viewer + Inferenz sind Register der Werkzeuge-Seite (2026-07-18);
      // URLs bleiben bookmarkbar und rendern Tools.svelte (Register-Switch).
      { path: "/system/db", tabWurzel: "/system/werkzeuge", label: "DB-Viewer", component: () => import("../pages/Tools.svelte"), hidden: true, inMenu: false, aliases: ["Database", "Datenbank", "SQL", "SQLite"] },
      { path: "/system/terminal", label: "Terminal", component: () => import("../pages/Terminal.svelte"), hidden: true, aliases: ["SSH", "Shell", "Konsole", "Console"] },
      // Inferenzdienst (docs/inferenz.md): seine Modelle und die Suche
      // nach Bedeutung (bge-m3) mit Probesatz. Bis 21.10.2026 „Ollama“.
      { path: "/system/inferenz", tabWurzel: "/system/werkzeuge", label: "Inferenz", component: () => import("../pages/Tools.svelte"), hidden: true, inMenu: false, aliases: ["AI", "KI", "Embedding", "bge-m3", "llama.cpp", "Modelle", "Ollama", "Qwen"] },
      // Werkzeug-Katalog: Registry aller Tools/Agents/Recovery/
      // Externals ("welche Tools gibt's hier?").
      // Abbild schreiben (24.10.2026, docs/abbild.md): Register der
      // Werkzeuge -- statt Raspberry Pi Imager auf dem Mac.
      { path: "/system/abbild", tabWurzel: "/system/werkzeuge", label: "Abbild schreiben", component: () => import("../pages/Tools.svelte"), hidden: true, inMenu: false, aliases: ["Flashen", "Image flashen", "SD-Karte beschreiben", "SD-Karte", "Kartenleser", "USB-Stick", "Raspberry Pi Imager", "rpi-imager", "balenaEtcher", "dd", "OpenWrt flashen", "Image auf Karte"] },
      { path: "/system/werkzeuge", label: "Werkzeuge", component: () => import("../pages/Tools.svelte"), hidden: true, aliases: ["Tool Catalog", "Registry", "Werkzeug-Katalog"] },
    ],
  },
  {
    // Daten (Slice 3 IA-Refactor): neue Section fuer Ablage. Files
    // und Vault sind beides "wo liegen Dokumente" -- konzeptionell
    // verwandt und gehoeren zusammen.
    title: "Daten",
    items: [
      {
        path: "/daten",
        label: "Daten",
        component: () => import("../pages/BereichsUebersicht.svelte"),
        aliases: ["Data", "Dokumente", "Documents", "Ablage"],
      },
      { path: "/daten/files", label: "Files", component: () => import("../pages/Files.svelte"), hidden: true, aliases: ["Dateien", "Documents", "Dokumente"] },
      // Ablage: Dokument einwerfen oder scannen, der Server sortiert
      // es in den Vault ein (docs/vault-ablage.md). Liegt bewusst bei
      // "Daten" und nicht bei den Diagnose-Werkzeugen unter System --
      // hier geht es um Dokumente, nicht um Betrieb (Tim 2026-07-29).
      { path: "/daten/ablage", label: "Ablage", component: () => import("../pages/Ablage.svelte"), hidden: true, aliases: ["Scannen", "Scan", "Dokument ablegen", "Einsortieren", "Posteingang", "Inbox"] },
      // Zeitreise: durch die gesicherten Staende blaettern (Time-Machine-
      // Geste -- der Pfad bleibt, die Zeit laeuft zurueck). Liegt bewusst
      // bei Daten und nicht bei den Backups: "Dann sind Backups Backups
      // und Daten bleiben Daten" (Tim 2026-09-04). Die Betriebssicht
      // (Matrix, kommt alles an) bleibt im Ketten-Blatt unter
      // /system/backups; beide Seiten verweisen aufeinander.
      { path: "/daten/zeitreise", label: "Zeitreise", component: () => import("../pages/Zeitreise.svelte"), hidden: true, aliases: ["Time Machine", "Versionen", "Fassungen", "frühere Fassung", "Stände", "Snapshots", "Verlauf", "Wiederherstellen", "Datei zurückholen", "Backup öffnen"] },
      { path: "/daten/vault", label: "Vault", component: () => import("../pages/Vault.svelte"), hidden: true, aliases: ["Passwörter", "Passwords", "Secrets", "Tresor"] },
      { path: "/daten/vault/browse", label: "Vault Browser", component: () => import("../pages/VaultBrowse.svelte"), hidden: true, aliases: ["Vault Files", "Vault Dateien"] },
      // Auswertungen: Sammelstelle fuer verdichtete Sichten auf den
      // Vault-Bestand. Zwischenseite statt Direkteintraege, weil davon
      // mehr dazukommen (Tim 2026-08-11).
      { path: "/daten/auswertungen", label: "Auswertungen", component: () => import("../pages/Auswertungen.svelte"), hidden: true, aliases: ["Analysen", "Statistik", "Reports", "Auswertung"] },
      // Entgelt-Auswertung (docs/entgelt.md): Verlauf von Gehalt,
      // Steuern und Versicherungen aus den Abrechnungen im Vault.
      // Hinter demselben PIN-Tor wie der Vault-Browser.
      //
      // inMenu:false -- der Einstieg laeuft ueber /daten/auswertungen.
      // Die Route bleibt auffindbar (Cmd-K, Aliase, Deeplinks), taucht
      // aber nicht als eigener Punkt in der Daten-Nav auf.
      { path: "/daten/entgelt", label: "Entgelt", component: () => import("../pages/Entgelt.svelte"), hidden: true, inMenu: false, aliases: ["Gehalt", "Lohn", "Abrechnung", "Entgeltabrechnung", "Verdienst", "Salary"] },
    ],
  },
  {
    // Claude im Homelab (16.10.2026, docs/claude.md): Chats mit Claude
    // Code auf forge. Am Schreibtisch ist der Baum die Übersicht (Chats je
    // Repo, darunter der Verbrauch); /claude ist dieselbe Übersicht als
    // Seite -- für das Telefon und für den Klick auf den Bereich. Ein Chat
    // ist eine eigene Seite (/claude/<id>, „neu" für einen neuen).
    title: "Claude",
    items: [
      { path: "/claude", label: "Claude", component: () => import("../pages/Claude.svelte"), aliases: ["KI", "AI", "Chat", "Assistent", "Claude Code", "Agent"] },
      // Das Gedächtnis (docs/gedaechtnis.md): frühere Chats, Merker, Doku
      // und Commits durchsuchen -- aus dem Baum (Abschnitt „Gedächtnis")
      // und über ⌘K. Fester Pfad, schlägt /claude/:id.
      { path: "/claude/gedaechtnis", label: "Gedächtnis", component: () => import("../pages/ClaudeGedaechtnis.svelte"), hidden: true, inMenu: false, aliases: ["Gedächtnis", "Erinnerung", "frühere Chats", "Chatverlauf durchsuchen", "Merker", "Memory"] },
      { path: "/claude/:id", label: "Chat", component: () => import("../pages/ClaudeChat.svelte"), hidden: true, inMenu: false, eigenerPfad: true },
      // Was ein Commit geändert hat bzw. was an einer offenen Datei noch nicht
      // committet ist -- aus den Abschnitten „Commits"/„Änderungen" im Baum.
      { path: "/claude/aenderung/:repo/:ziel", label: "Änderung", component: () => import("../pages/ClaudeAenderung.svelte"), hidden: true, inMenu: false },
      // Ein Bericht der Repo-Prüfung über die Claude-API (docs/ki.md) -- aus
      // dem Abschnitt „Repo-Prüfungen" der Übersicht.
      { path: "/claude/bericht/:datei", label: "Bericht", component: () => import("../pages/ClaudeBericht.svelte"), hidden: true, inMenu: false },
    ],
  },
  {
    // Einstellungen. Bis 03.10.2026 hiess die Gruppe "Profil" und hatte
    // eine eigene Uebersicht (/profil) samt Konto-Symbol in der Leiste.
    // Die Uebersicht bot nichts, was das Einstellungsfenster nicht auch
    // verlinkt (Tim: "Brauchen wir das Profil noch?") -- sie ist weg,
    // die vier Flaechen liegen unter /einstellungen/…, die alten
    // Adressen leiten um.
    title: "Einstellungen",
    items: [
      // Das Einstellungsfenster (docs/einstellungen-inventar.md): ein
      // durchsuchbarer Index ueber alles, was einstellbar ist. Die
      // Flaechen (Hosts, SSH, Geheimnisse) bleiben ihre eigenen Seiten,
      // der Index verweist nur darauf.
      { path: "/einstellungen", label: "Einstellungen", component: () => import("../pages/Einstellungen.svelte"), hidden: true, inMenu: false, aliases: ["Settings", "Optionen", "Konfiguration", "Preferences", "Profil", "Profile", "Push", "Benachrichtigungen", "Notifications", "Web Push", "Theme", "Dark Mode", "Erscheinungsbild", "Aussehen", "Anmeldung", "Login", "Passkey", "Passkeys", "Recovery", "Session", "2FA", "Konto", "Account"] },
      // Anmeldung, Push, Suche und Erscheinungsbild waren bis 04.10.2026
      // eigene Seiten. Sie sind Flaechen/Karten im Einstellungsfenster
      // (Manifest `inhalt` bzw. `karte`), die alten Adressen leiten um.
    ],
  },
];

// Redirects: alte URL → neue URL. Der Hash-Router prueft beim Wechsel
// ueber `findRedirect` und navigiert ggf. weiter. Ein Eintrag pro
// umgezogener Route reicht; sub-paths werden nicht automatisch
// mitgemappt, sondern muessen einzeln gelistet werden (heute keiner
// betroffen). Generisch erweiterbar fuer kuenftige IA-Refactors.
//
// Lebensdauer: mind. ein Release nach dem Move; danach kann ein
// Eintrag entfallen, wenn aussenstehende Bookmarks vermutlich
// migriert sind.
export const redirects: Record<string, string> = {
  // Nav-Konzept 2026-07-18: IoT -> Zuhause, Aufgaben+Diagnose -> System.
  // Jeder alte Pfad zeigt DIREKT auf sein kanonisches Ziel (findRedirect
  // ist single-step, Ketten wuerden ins Leere laufen).
  "/iot": "/zuhause",
  "/iot/sensoren": "/zuhause/sensoren",
  "/iot/aktoren": "/zuhause/aktoren",
  "/iot/aktoren/acinfinity": "/zuhause/aktoren/acinfinity",
  "/iot/climate": "/zuhause/climate",
  "/iot/co2monitor": "/zuhause/co2monitor",
  "/iot/lipoguard": "/zuhause/lipoguard",
  "/iot/waterleak": "/zuhause/waterleak",
  "/iot/wetter": "/zuhause/wetter",
  "/iot/wetter/blitze": "/zuhause/wetter/blitze",
  "/iot/wetter/karte": "/zuhause/wetter/karte",
  "/iot/energy": "/zuhause/energy",
  "/iot/ambient": "/zuhause/ambient",
  "/iot/geraete": "/zuhause/geraete",
  "/iot/integrationen/zwave": "/zuhause/geraete",
  "/iot/integrationen/zigbee": "/zuhause/geraete",
  "/iot/integrationen/esphome": "/zuhause/geraete",
  "/iot/settings": "/zuhause",
  "/iot/settings/ha": "/einstellungen",
  "/iot/settings/acinfinity": "/einstellungen",
  "/aufgaben": "/system",
  "/aufgaben/automatisierungen": "/system/automatiken",
  "/tasks": "/system/tasks",
  "/runs": "/system/runs",
  "/backups": "/system/backups",
  "/diagnose": "/system",
  "/diagnose/logs": "/system/logs",
  "/diagnose/events": "/system/events",
  "/diagnose/db": "/system/db",
  "/diagnose/terminal": "/system/terminal",
  "/diagnose/ollama": "/system/inferenz",
  "/system/ollama": "/system/inferenz",
  "/diagnose/katalog": "/system/werkzeuge",
  // 2026-07-17/18: Integrationen-Ebene (HA-Erbe) komplett durch die
  // vereinheitlichte Geraete-Seite ersetzt -- auch die Protokoll-
  // Detail-Pages (Zwave/Zigbee/Esphome) sind aufgeloest, ihre Reste
  // (LQI, Schalten, Spannung/Strom, Deaktivierte) leben in der Tabelle.
  "/iot/integrationen": "/zuhause/geraete",
  "/zuhause/integrationen/zwave": "/zuhause/geraete",
  "/zuhause/integrationen/zigbee": "/zuhause/geraete",
  "/zuhause/integrationen/esphome": "/zuhause/geraete",
  "/infra/adguard": "/sicherheit/dns",
  // Link-Health ist seit 02.10.2026 das Register "Verlauf" der Netzwerk-Seite.
  "/infra/link-health": "/infra/network/verlauf",
  "/infra/lightning": "/zuhause/wetter",
  "/iot/acinfinity": "/zuhause/aktoren/acinfinity",
  "/iot/automation": "/system/automatiken",
  // Slice 3 IA-Refactor 2026-05-14: Tools-Section -> Diagnose (heute
  // System), Files+Vault -> Daten. Bookmarks ueberleben durch diese
  // Aliase.
  "/tools": "/system/werkzeuge",
  "/tools/logs": "/system/logs",
  "/tools/events": "/system/events",
  "/tools/db": "/system/db",
  "/tools/terminal": "/system/terminal",
  "/tools/files": "/daten/files",
  "/vault": "/daten/vault",
  // Slice 4 IA-Refactor 2026-05-14: Settings-Section aufgeloest.
  "/settings/general": "/einstellungen",
  "/settings/theme": "/einstellungen",
  // Die Wert-Register sind im Einstellungsfenster aufgegangen
  // (16.08., docs/einstellungen-inventar.md, Schritt 5). Lesezeichen
  // und alte Verweise landen dort, nicht auf einer 404.
  // Die Wert-Register sind im Einstellungsfenster aufgegangen
  // (16.08., docs/einstellungen-inventar.md, Schritt 5). Lesezeichen
  // und alte Verweise landen dort, nicht auf einer 404.
  "/profil/allgemein": "/einstellungen",
  // Profil aufgeloest (03.10.2026): die Uebersicht entfaellt, die vier
  // Flaechen liegen unter /einstellungen.
  "/profil": "/einstellungen",
  "/profil/erscheinungsbild": "/einstellungen",
  "/profil/suche": "/einstellungen",
  "/profil/zugang": "/einstellungen",
  "/profil/push": "/einstellungen",
  // Push ist seit 04.10.2026 eine Karte im Einstellungsfenster.
  "/einstellungen/push": "/einstellungen",
  "/einstellungen/erscheinungsbild": "/einstellungen",
  "/einstellungen/suche": "/einstellungen",
  "/einstellungen/anmeldung": "/einstellungen",
  // Register der Fach-Seiten, die nur Einstellungen waren: seit 04.10.2026
  // Flaechen im Einstellungsfenster (Manifest `inhalt`).
  "/infra/cluster/inventar": "/einstellungen",
  "/infra/cluster/ssh": "/einstellungen",
  "/infra/agents/mqtt": "/einstellungen",
  "/infra/storage/alarme": "/einstellungen",
  "/infra/storage/bibliotheken": "/einstellungen",
  "/infra/status/ziele": "/einstellungen",
  "/zuhause/geraete/ha": "/einstellungen",
  "/infra/network/interfaces": "/einstellungen",
  "/sicherheit/dns/settings": "/einstellungen",
  "/zuhause/aktoren/acinfinity/cloud": "/einstellungen",
  "/settings/hosts": "/einstellungen",
  "/settings/servers": "/einstellungen",
  "/settings/mqtt": "/einstellungen",
  "/settings/storage-monitors": "/einstellungen",
  "/settings/auth": "/einstellungen",
  "/settings/push": "/einstellungen",
  "/sicherheit/zugang": "/einstellungen",
  "/sicherheit/push": "/einstellungen",
  "/settings/adguard": "/einstellungen",
  "/settings/acinfinity": "/einstellungen",
  "/sicherheit/settings/adguard": "/einstellungen",
  "/security/inspector": "/sicherheit/forensik",
  // Einstellungen-Dedupe 2026-07-18: Settings-Hubs aufgeloest, jede
  // Settings-URL zeigt auf ihr Register auf der Domain-Seite.
  "/infra/settings": "/infra",
  "/infra/settings/hosts": "/einstellungen",
  "/infra/settings/ssh": "/einstellungen",
  "/infra/settings/mqtt": "/einstellungen",
  "/infra/settings/storage-alarme": "/einstellungen",
  "/infra/settings/uptime": "/einstellungen",
  "/zuhause/settings": "/zuhause",
  "/zuhause/settings/ha": "/einstellungen",
  "/zuhause/settings/acinfinity": "/einstellungen",
  "/settings/ha": "/einstellungen",
  "/iot/aktoren/acinfinity/settings": "/einstellungen",
  // Werkstatt (18.10.2026): der 3D-Drucker zog aus Zuhause um. Alte
  // Lesezeichen und schon zugestellte Pushs landen so weiter beim Drucker.
  "/zuhause/drucker": "/werkstatt/drucker",
};

/** tabWurzelFuer sagt, zu WELCHER Seite ein Pfad gehoert: fuer ein
 *  Register die Seite, auf der es sitzt -- sonst der Pfad selbst.
 *  Die Shell macht daraus "derselbe Tab". */
export function tabWurzelFuer(path: string): string {
  return findRoute(path)?.route.tabWurzel ?? path;
}

export function findRedirect(path: string): string | null {
  return redirects[path] ?? null;
}

// routeIntents: verb-reiche "was man hier tut"-Zeile je Route-Pfad.
// Unsichtbar in der UI (wie aliases), nur fuer die Suche -- gibt
// natuerlichsprachigen Absichts-Queries ("router neu flashen", "warum
// ist DNS rot") einen Anker auf der Zielseite, den Label+Aliase (reine
// Substantive) nicht liefern. Zentral gehalten statt inline, damit der
// ganze Satz auf einen Blick redigierbar ist. Fehlt ein Pfad, faellt
// die Route auf substantiv-only zurueck (kein Regress). Fliesst in den
// Embed-Text (Semantik) UND als Fuzzy-intent-Haystack (Fallback
// ohne Inferenzdienst). Intent-Layer Slice 2, 2026-07-06.
export const routeIntents: Record<string, string> = {
  // Infrastruktur
  "/infra": "Hardware und Server im Ueberblick pruefen, Infrastruktur-Zustand",
  "/infra/cluster": "Server und Hosts ansehen, Maschinen im Cluster pruefen, einzelnen Host oeffnen oder neu starten",
  "/infra/network": "Router und Internet-Verbindung pruefen, OpenWrt ansehen, WAN und LAN, DDNS, Portfreigaben, IPv6-Prefix, Router neu starten",
  "/infra/docker": "Container ansehen und verwalten, Docker-Stacks pruefen, laufende Container finden, Container neu starten oder updaten",
  "/infra/docker/stacks": "Compose-Stacks ansehen und bearbeiten, compose-Datei aendern, Stack neu ausrollen oder herunterfahren, welche Stacks laufen auf welchem Rechner",
  "/infra/docker/netzwerke": "Docker-Netzwerke ansehen, welcher Container haengt an welchem Netz, Bridge und Subnetz pruefen, unbenutzte Netze finden",
  "/infra/docker/aufraeumen": "Docker aufraeumen, alte Images und unbenutzte Volumes loeschen, Platz auf der Platte freimachen, was darf weg und was bleibt, prune",
  "/infra/storage": "Speicherplatz und Festplatten pruefen, ZFS-Pools, SMART-Werte, Snapshots verwalten, Datasets anlegen, ist die Platte voll",
  "/infra/flash": "Geraet flashen und Firmware aufspielen, ESPHome oder Shelly neu bespielen, kaputtes Geraet tauschen, Sensor per FTDI beschreiben, OTA-Update ausrollen",
  "/infra/agents": "MQTT-Agenten ansehen, Edge-Agenten auf Geraeten pruefen und ausrollen",
  "/infra/network/verlauf": "Netzwerk-Verbindungen und Carrier pruefen, Ethernet-Link-Status, Verbindungsabbruch diagnostizieren, Link-Verlauf je Rechner",
  "/infra/topologie": "Netzwerk-Karte ansehen, wie Hosts und Cluster verbunden sind, Topologie-Graph",
  "/infra/status": "Erreichbarkeit und Verbindungen pruefen, ist alles online, URL-Uptime, Link-Status, was ist gerade rot",
  "/infra/netzfrequenz": "Netzfrequenz im Stromnetz ansehen, wie stabil ist das Netz, Abweichung von 50 Hz, Verlauf pruefen, droht ein Blackout",
  // Sicherheit
  "/sicherheit": "Sicherheit und Perimeter pruefen, Angriffe, Logins und DNS-Filter im Ueberblick",
  "/security": "Login-Versuche und fail2ban ansehen, gesperrte IPs, Auth-Versuche, Bans",
  "/sicherheit/geheimnisse": "wo steht das Passwort fuer einen Dienst, Zugangsdaten und API-Token hinterlegen, Geheimnis nachschlagen oder aendern, Schluessel fuer fremde Dienste verwalten",
  "/sicherheit/forensik": "IPs untersuchen, Whitelist pflegen, Sicherheits-Werkzeuge, Angreifer analysieren",
  "/sicherheit/dns": "AdGuard und DNS-Filter pruefen, Querylog ansehen, Domain freigeben oder sperren, Schutz pausieren, Filterlisten aktualisieren, warum ist DNS rot",
  "/sicherheit/netsec": "Geraete im Netz erkennen, neue MACs, IP-Sperrliste, CrowdSec, unbekanntes Geraet finden, Geraete je Router und Standort, Router anlernen",
  // IoT
  "/zuhause": "Smart Home und Hausautomatisierung, Sensoren, Aktoren und Wetter im Ueberblick",
  "/zuhause/sensoren": "Messwerte ansehen, Klima, CO2, Temperatur und Feuchte, Sensoren pruefen",
  "/zuhause/aktoren": "Schalter und Steckdosen bedienen, Geraete ein- und ausschalten, Aktoren steuern",
  "/zuhause/aktoren/acinfinity": "Luefter im Grow-Zelt steuern, AC Infinity regeln",
  "/zuhause/climate": "Temperatur und Luftfeuchtigkeit je Raum ansehen",
  "/zuhause/co2monitor": "CO2 und Luftqualitaet ansehen",
  "/zuhause/funkmelder": "Bewegung im Raum ueber das WLAN erkennen, bewegt sich gerade jemand, Bewegungswert und Schwelle des Funkmelders ansehen, wann war zuletzt Bewegung",
  "/zuhause/grundriss": "Grundriss der Wohnung ansehen, Raeume und Flaechen, Plan oder raeumliche Ansicht, wo steht welches Geraet",
  "/zuhause/lipoguard": "LiPo-Akku-Spannung ueberwachen, Batterie pruefen",
  "/zuhause/waterleak": "Wassermelder pruefen, Leckage-Alarme, Wasserleck",
  "/zuhause/wetter": "Wettervorhersage und aktuelle Lage ansehen, Blitze und Gewitter",
  "/zuhause/wetter/blitze": "Blitzeinschlaege und Gewitter ansehen, Alert-Radius einstellen",
  "/zuhause/wetter/karte": "Wetterkarte, Regenradar und Niederschlag ansehen",
  "/zuhause/energy": "Stromverbrauch und PV oder Solar ansehen, Energie-Verbrauch pruefen",
  "/zuhause/ambient": "Lichter und Szenen schalten, Hue, Ambient-Beleuchtung, alles aus",
  "/zuhause/geraete": "Alle IoT-Geraete vereinheitlicht ansehen, Verlauf, Zigbee, Z-Wave, ESPHome",
  // Werkstatt
  "/werkstatt": "was gebaut wird, Konstruktionen und 3D-Drucker im Überblick, konstruieren und drucken",
  "/werkstatt/konstruktionen": "Konstruktionen ansehen, drehen, schneiden und messen, Teile ein- und ausblenden, Stückliste und Erkenntnisse öffnen, ist die Prüfung grün",
  "/zuhause/kalender": "wann ist was, Termin eintragen, an etwas erinnern lassen, was steht an, Wartung oder Prüfung vormerken",
  "/werkstatt/kaufteile": "welche Schrauben, Muttern, Scheiben und Gewindeeinsätze sind da, was fehlt, was muss gekauft werden, Einbaumaße ruthex, Sortimente",
  "/werkstatt/drucker": "was druckt der 3D-Drucker gerade, wie lange noch, wann ist der Druck fertig, welches Filament ist im AMS, Kamerabild ansehen, Druck pausieren oder abbrechen, Licht im Drucker schalten, welche Drucke liefen, Wartung des Druckers: was ist fällig, Riemen nachspannen, Stangen reinigen, Spindeln fetten, Kohlefilter tauschen, Druckstunden",
  // Aufgaben
  "/system/tasks": "Cron-Jobs und Tasks ansehen, manuell starten, Zeitplaene, Task-Verlauf",
  "/system/backups": "Backup-Datenfluss aller Ketten, Snapshot bis Offsite, Host/Cluster, Backup jetzt starten",
  "/system/backups/proben": "funktioniert das Backup im Ernstfall, kommt alles zurueck, Restore testen, Wiederherstellung pruefen, wie lange dauert die Wiederherstellung, wie viel ginge verloren, ist die Kopie gleich wie das Original",
  "/system/backups/abdeckung": "was liegt auf welchem Rechner, ist alles gesichert, was hat kein Backup, welche Daten sind ungesichert, wie viele Kopien gibt es, bewusst nicht sichern",
  "/system/archiv": "was ist archiviert, wo liegt das alte Home Assistant, abgeschaltete Dienste aufbewahren, Archiv zurueckholen oder loeschen, ist das Archiv gesichert, Reste aufgeloester Backup-Ketten, verwaiste Backup-Daten aufraeumen",
  "/system/abbild": "SD-Karte beschreiben, Image flashen, OpenWrt auf die Karte, Raspberry Pi Imager ersetzen, was steckt im Kartenleser, USB-Stick bootfaehig machen",
  "/system/backups/network": "network ist kaputt, SD-Karte tot, Recovery-Tool wiederherstellen, Raspberry Pi neu aufsetzen, Karte beschreiben",
  "/system/runs": "Task-Laeufe und Verlauf ansehen, was lief wann, Fehler nachsehen",
  "/system/automatiken": "Automatiken ansehen und bearbeiten, Trigger einstellen",
  // Diagnose
  "/system": "Tasks, Cron-Jobs, Automatiken, Backups und Diagnose-Werkzeuge, Logs, Terminal, Datenbank, laeuft der Laden",
  "/system/server": "wie geht es dem Server, Last und Speicher von forge, Temperatur, wie gross ist die Datenbank, Schreibrate, Tabellen, Container, Journal, Version, Dashboard neu starten",
  "/system/logs": "Server-Logs live ansehen und filtern, Protokolle, Journal",
  "/system/meldungen": "offene Warnungen und Hinweise durchgehen, was meckert gerade, Meldung abhaken oder stummschalten, warum blinkt das Achtungssymbol",
  "/system/events": "Ereignisse und Audit-Log ansehen, was ist passiert",
  "/system/db": "Datenbank ansehen, SQL ausfuehren, SQLite-Tabellen",
  "/system/terminal": "SSH-Terminal oeffnen, Shell auf einem Host, Konsole",
  "/system/inferenz": "KI-Modelle auf forge ansehen, welches Modell ist geladen, laeuft es auf GPU oder CPU, Embedding der Suche pruefen, bge-m3, Qwen",
  "/system/werkzeuge": "alle Werkzeuge, Tasks, Recovery-Tools und Agenten finden, Werkzeug-Katalog",
  "/claude": "mit Claude chatten, Claude etwas bauen oder reparieren lassen, Frage an die KI stellen, laufende Chats ansehen, Claude anmelden",
  "/claude/gedaechtnis": "in früheren Chats suchen, was war nochmal, wie haben wir das gelöst, Entscheidung nachlesen, Merker und Doku durchsuchen",
  // Daten
  "/daten": "Dateien und Dokumente, Ablage und Vault im Ueberblick",
  "/daten/files": "Dateien verwalten, Dateimanager, SSH-Mount, zwischen Hosts kopieren",
  "/daten/ablage": "wo lege ich das Dokument hin, Papier scannen und einsortieren, Rechnung oder Brief ablegen, Posteingang abarbeiten",
  "/daten/zeitreise": "wie sah der Ordner letzte Woche aus, alte Fassung einer Datei holen, geloeschte Datei zurueckholen, durch die Backup-Staende blaettern, Datei von gestern, frueherer Stand",
  "/daten/vault": "Passwoerter und Secrets im Tresor, Vault oeffnen",
  "/daten/vault/browse": "Vault-Dateien durchsuchen",
  "/daten/auswertungen": "Verdichtete Sichten auf den Vault-Bestand",
  "/daten/entgelt": "Gehaltsentwicklung, Steuern, Kranken- und Pflegeversicherung",
  // Argus (module/argus/routen.ts)
  ...argusAbsichten,
  // Medien (module/medien/routen.ts)
  ...medienAbsichten,
  // Einstellungen + Profil
  "/einstellungen": "wo stelle ich etwas ein, Einstellung finden und aendern, Konfiguration nachschlagen, Optionen der Anlage durchsuchen, Push-Benachrichtigungen einrichten und testen, iOS Web-Push, Passkeys und Anmeldung verwalten, angemeldete Geraete abmelden, Theme hell oder dunkel, Suche personalisieren",
};

// semanticIndexEntries: flache Liste fuer den Embedding-Index der
// TopBar-Suche. Param-Routen (mit ":") und Dashboard ueberspringen --
// Dashboard ist eh ueber das Logo/Home erreichbar. Alle anderen Pages
// inkl. inMenu:false (Profil-Sub-Pages) sind drin, damit die Suche
// sie findet. Text-Format: "label aliases section" -- bge-m3 kommt
// mit Bag-of-Words gut klar.
//
// `key` ist die Embedding-Cache-Adresse + Lookup-ID aus dem FE; fuer
// Routen ist das deren Pfad. Andere Entity-Typen (Tasks etc.) bauen
// ihre eigenen Keys (z.B. "task:wob-data-backup").
export function semanticIndexEntries(): Array<{ key: string; text: string }> {
  const out: Array<{ key: string; text: string }> = [];
  for (const sec of sections) {
    for (const r of sec.items) {
      if (r.path.includes(":")) continue;
      if (r.path === "/") continue;
      const aliases = (r.aliases ?? []).join(" ");
      const intent = routeIntents[r.path] ?? "";
      const text = [r.label, aliases, sec.title, intent].filter(Boolean).join(" ").trim();
      out.push({ key: r.path, text });
    }
  }
  return out;
}

// findRoute returns the matched RouteItem plus extracted path params
// (e.g. "/infra/cluster/:host_id" matched against
// "/infra/cluster/macbook" → params = { host_id: "macbook" }). Static
// routes (no `:` in the path) match only on exact equality and yield
// an empty params map. Static matches take precedence over dynamic
// ones — a literal "/infra/cluster" wins over the parametrised
// "/infra/cluster/:host_id" because the former has zero variable
// segments.
export function findRoute(
  path: string,
): { route: RouteItem; params: Record<string, string> } | null {
  // Pass 1: exact match.
  for (const s of sections) {
    for (const i of s.items) {
      if (i.path === path) return { route: i, params: {} };
    }
  }
  // Pass 2: param match (`:name` segments).
  const segments = path.split("/").filter(Boolean);
  for (const s of sections) {
    for (const i of s.items) {
      if (!i.path.includes(":")) continue;
      const routeSegments = i.path.split("/").filter(Boolean);
      if (routeSegments.length !== segments.length) continue;
      const params: Record<string, string> = {};
      let match = true;
      for (let k = 0; k < routeSegments.length; k++) {
        const seg = routeSegments[k];
        if (seg.startsWith(":")) {
          params[seg.slice(1)] = decodeURIComponent(segments[k]);
        } else if (seg !== segments[k]) {
          match = false;
          break;
        }
      }
      if (match) return { route: i, params };
    }
  }
  return null;
}

// Idle-Prefetch: laedt alle Page-Chunks nacheinander im Hintergrund,
// sobald der Browser Luft hat. Damit ist Navigation nach dem Boot
// genauso instant wie vor dem Code-Splitting, und die PWA hat alle
// Chunks lokal. Sequenziell statt parallel, mit Start-Verzoegerung:
// der Boot-Burst der Summary-Stores hat Vorrang. Safari kennt kein
// requestIdleCallback -- dort drosselt der setTimeout-Fallback.
export function prefetchAllPages(startDelayMs = 4000): void {
  const loaders = sections.flatMap((s) => s.items.map((i) => i.component));
  const idle = (cb: () => void): void => {
    if ("requestIdleCallback" in window) {
      window.requestIdleCallback(() => cb(), { timeout: 2000 });
    } else {
      setTimeout(cb, 250);
    }
  };
  let i = 0;
  const next = (): void => {
    if (i >= loaders.length) return;
    const load = loaders[i++];
    void load()
      .catch(() => {}) // einzelner Chunk-Fehler bricht den Sweep nicht ab
      .finally(() => idle(next));
  };
  setTimeout(() => idle(next), startDelayMs);
}
